import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BASE_URL = "https://nissi-india.com";
const LOGO_URL = "/assets/company-logo.png"; // change if needed

const monthLabelFromValue = (v) => {
  const map = {
    "1": "January",
    "2": "February",
    "3": "March",
    "4": "April",
    "5": "May",
    "6": "June",
    "7": "July",
    "8": "August",
    "9": "September",
    "10": "October",
    "11": "November",
    "12": "December",
  };
  if (!v || v === "Select") return "";
  return map[String(v)] || String(v);
};

const toNum = (x) => {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
};

const fmtMoney = (n) => {
  const num = toNum(n);
  return num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const SalaryModeText = (row) => {
  // "Through Bank" shown in screenshot.
  // If backend returns paymode/pay_mode, prefer it.
  const raw =
    (row?.paymode ?? row?.pay_mode ?? row?.fld_paymode ?? row?.fld_pay_mode ?? "")
      .toString()
      .toLowerCase();

  if (raw.includes("cash")) return "Cash";
  if (raw.includes("bank")) return "Through Bank";

  // Fallback: if account no exists and not NIL -> bank else cash
  const acc = (row?.fld_accountno ?? row?.accountno ?? "").toString().trim().toUpperCase();
  if (acc && acc !== "NIL" && acc !== "0") return "Through Bank";
  return "Cash";
};

const SpecialHraReportPrint = () => {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);

  const finYearId = params.get("finYearId") || localStorage.getItem("finYearId") || "";
  const loginBranchId = params.get("loginBranchId") || localStorage.getItem("branchId") || "";
  const branchType = params.get("branchType") || localStorage.getItem("branchType") || "";
  const branchId = params.get("branchId") || "Select";

  const month = params.get("month") || "Select"; // number 1..12 or Select
  const year = params.get("year") || "Select";
  const fromDate = params.get("fromDate") || "";
  const toDate = params.get("toDate") || "";
  const empCode = params.get("empCode") || "";
  const empName = params.get("empName") || "";

  const monthName = monthLabelFromValue(month);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const branchName = useMemo(() => {
    // Prefer data row branch name; else show HEAD-OFFICE for HO
    if (rows?.length && rows[0]?.fld_branchname) return rows[0].fld_branchname;
    if ((branchType || "").toUpperCase() === "HO") return "HEAD-OFFICE";
    return "BRANCH";
  }, [rows, branchType]);

  const totalAmt = useMemo(() => {
    return rows.reduce((sum, r) => sum + toNum(r?.fld_specialhra), 0);
  }, [rows]);

  const fetchPrintData = async () => {
    if (!String(finYearId).trim()) return toast.warn("Missing finYearId.");
    if (!String(loginBranchId).trim()) return toast.warn("Missing loginBranchId.");
    if (!String(year).trim() || year === "Select") return toast.warn("Year is required.");

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/special-hra-report/print-data`, {
        params: {
          finYearId: Number(finYearId),
          loginBranchId: Number(loginBranchId),
          branchType: String(branchType),
          branchId: String(branchId),
          month: String(month),
          year: String(year),
          fromDate: String(fromDate),
          toDate: String(toDate),
          empCode: String(empCode),
          empName: String(empName),
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTimeout(() => window.print(), 400);
      } else {
        toast.error(res?.data?.message || "Failed to load print data.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrintData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="printRoot">
      <ToastContainer position="top-right" autoClose={2200} />

      <div className="pageFrame">
        {/* HEADER */}
        <div className="headerBox">
          <div className="logoCol">
            <img
              src={LOGO_URL}
              alt="Logo"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div>

          <div className="companyCol">
            {/* You can replace these with DB company info later */}
            <div className="companyName">Fresh Mobile Licenses</div>
            <div className="companySub">CIVIL CONSTRUCTION</div>
            <div className="companyLine">
              SHREE GAJANAN MAHARAJ MANDIR-SQUARE NAGAPUR RD-
            </div>
            <div className="companyLine">
              Phone No : 8698163331 Email : sgpiplproject@gmail.com
            </div>
          </div>
        </div>

        {/* TITLE STRIP */}
        <div className="titleStrip">
          <div className="titleLeft">
            SPECIAL HRA FOR THE MONTH {monthName ? monthName.toUpperCase() : ""}
          </div>
          <div className="titleMid">- {year}</div>
          <div className="titleRight">{String(branchName || "").toUpperCase()}</div>
        </div>

        {/* TABLE */}
        <div className="tableWrap">
          <table className="reportTable">
            <thead>
              <tr>
                <th className="wSno">S.No</th>
                <th className="wCode">Code</th>
                <th className="wEmp">Employee Name</th>
                <th className="wDesig">Designation</th>
                <th className="wAcc">Acc Number</th>
                <th className="wHra">HRA</th>
                <th className="wMode">Through Bank</th>
              </tr>
            </thead>

            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="noData">
                    No Records Found
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => (
                  <tr key={`${r.fld_employeecode || idx}-${idx}`}>
                    <td className="center">{idx + 1}</td>
                    <td className="center">{r.fld_employeecode || ""}</td>
                    <td className="left">{r.fld_employee || ""}</td>
                    <td className="center">{r.fld_designationname || ""}</td>
                    <td className="center">{r.fld_accountno || r.accountno || ""}</td>
                    <td className="right">{fmtMoney(r.fld_specialhra)}</td>
                    <td className="center">{SalaryModeText(r)}</td>
                  </tr>
                ))
              )}

              {/* TOTAL ROW (right-side box like screenshot) */}
              <tr>
                <td colSpan={4} className="blankCell" />
                <td className="totalLabel center">
                  <b>Total Amount</b>
                </td>
                <td className="totalValue right">
                  <b>{fmtMoney(totalAmt)}</b>
                </td>
                <td className="blankCell" />
              </tr>
            </tbody>
          </table>
        </div>

        {/* FOOT SIGNATURES */}
        <div className="signRow">
          <div className="signCellBox">Prepared By</div>
          <div className="signCellBox">Checked By</div>
          <div className="signCellBox">Sr.Mgr Accounts</div>
        </div>

        {loading && <div className="loadingNote">Loading print data...</div>}
      </div>

      <style>{`
        body { margin: 0; font-family: Arial, sans-serif; }

        .printRoot {
          background: #eee;
          padding: 18px;
        }

        .pageFrame {
          background: #fff;
          border: 1px solid #333;
          width: 100%;
          max-width: 1100px;
          margin: 0 auto;
          padding: 18px;
        }

        .loadingNote { text-align: center; margin-top: 10px; }

        /* HEADER */
        .headerBox {
          border: 1px solid #000;
          display: grid;
          grid-template-columns: 170px 1fr;
          align-items: center;
          gap: 10px;
          padding: 12px;
        }

        .logoCol { display: flex; justify-content: center; align-items: center; }
        .logoCol img { max-width: 140px; max-height: 85px; object-fit: contain; }

        .companyCol { text-align: center; }
        .companyName { font-weight: 800; font-size: 22px; }
        .companySub { font-weight: 800; font-size: 14px; margin-top: 4px; }
        .companyLine { font-size: 12px; margin-top: 2px; }

        /* TITLE STRIP */
        .titleStrip {
          margin-top: 14px;
          border-top: 1px solid #000;
          border-bottom: 1px solid #000;
          padding: 10px 6px;
          display: grid;
          grid-template-columns: 1fr 120px 1fr;
          align-items: center;
          font-weight: 800;
          font-size: 18px;
        }

        .titleLeft { text-align: center; }
        .titleMid { text-align: center; }
        .titleRight { text-align: center; }

        /* TABLE */
        .tableWrap { overflow: visible !important; margin-top: 14px; }

        .reportTable {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
          font-size: 12px;
        }

        .reportTable th,
        .reportTable td {
          border: 1px solid #000;
          padding: 8px 8px;
          vertical-align: middle;
          word-wrap: break-word;
        }

        .reportTable th {
          font-weight: 800;
          text-align: center;
          white-space: nowrap;
        }

        .left { text-align: left; }
        .center { text-align: center; }
        .right { text-align: right; }

        .noData { text-align: center; padding: 16px; }

        .blankCell { background: #fff; }

        .totalLabel { font-size: 12px; }
        .totalValue { font-size: 12px; }

        /* COLUMN WIDTHS */
        .wSno { width: 70px; }
        .wCode { width: 110px; }
        .wEmp { width: 260px; }
        .wDesig { width: 220px; }
        .wAcc { width: 140px; }
        .wHra { width: 120px; }
        .wMode { width: 160px; }

        /* SIGNATURES */
        .signRow {
          margin-top: 70px;
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 10px;
          align-items: center;
        }

        .signCellBox {
          text-align: left;
          font-weight: 700;
          font-size: 13px;
          padding-top: 18px;
        }

        /* PRINT */
        @media print {
          .printRoot { background: #fff; padding: 0; }
          .pageFrame { border: none; max-width: none; padding: 0; }
          .tableWrap { overflow: visible !important; }

          @page {
            size: A4 landscape;
            margin: 10mm;
          }
        }
      `}</style>
    </div>
  );
};

export default SpecialHraReportPrint;
